/*
#############################################################################
# Copyright (c) 2022-2023 NVIDIA CORPORATION & AFFILIATES. All rights reserved.
#
# NVIDIA CORPORATION, its affiliates and licensors retain all intellectual
# property and proprietary rights in and to this material, related
# documentation and any modifications thereto. Any use, reproduction,
# disclosure or distribution of this material and related documentation
# without an express license agreement from NVIDIA CORPORATION or
# its affiliates is strictly prohibited.
###############################################################################
*/

/* eslint-disable no-param-reassign */
/* eslint-disable no-loop-func */
/* eslint-disable no-use-before-define */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { useSnackbar } from "notistack";
import DialogContentText from "@mui/material/DialogContentText";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import { useSelector, useDispatch } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import config, { WEBRTC_STATUS } from "../../../config/config";
import VideoCaption from "../../../features/ttsText/TtsText";
import { webrtcActions } from "../webrtcSlice";
import { webSocketActions } from "../../websocket/websocketSlice";

const uiServerOrVstEndpointString = config.useVstApiDirectly ? `${config.vstEndpoint}/api` : `${config.uiServerEndpoint}/vms`;

const PEER_CONNECTION_OPTIONS = { optional: [{ DtlsSrtpKeyAgreement: true }] };
const OFFER_OPTIONS = { offerToReceiveAudio: true, offerToReceiveVideo: true };
const MAX_RETRIES = 20;
let stream;

function Webrtc({pMode, micEnabled, receivedWebsocketMessage}) {
  const dispatch = useDispatch();
  const avatarVideoRef = useRef();
  const earlyCandidates = useRef([]);
  const pc = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const peerId = useRef();
  const restartCount = useRef(0);
  // used in test to detect if webrtc is receiving frames
  const webrtcStatsRef = useRef();
  const [isVideoPlaying, setPlayingStatus] = useState("avatar-not-playing");
  const [open, setOpen] = React.useState(false);
  const webrtcStatus = useSelector(
    (state) => state.webrtcStatus.webrtcStatus
  );
  const sessionTime = useSelector((state) => state.session.sessionTime);
  const [counter, setCounter] = React.useState(sessionTime);

  const audioBitrate = useRef();
  const degradationPreference = useRef("resolution");
  const maxFps = useRef();

  const videoPlayerStyles = {
    position: "relative",
    padding: 20,
    objectFit: "contain",
    height: pMode ? "100%" : "609px",
    background:
      "radial-gradient(53.31% 53.31% at 49.95% 100%, #000000 0%, rgba(0, 0, 0, 0) 100%)",
  };

  useEffect(() => {
    startWebrtcConnection();
    return () => {
      stopWebrtcConnection();
    };
  }, []);
  
  useEffect(() => {
    toggleMicrophone(micEnabled)
  }, [micEnabled])

  useEffect(() => {
    if (webrtcStatus === WEBRTC_STATUS.ERROR) {
      enqueueSnackbar(
        "Failed to capture and send web cam stream, please refresh page",
        { variant: "error" }
      );
      dispatch(webSocketActions.setStreamId(""));
    }
  }, [webrtcStatus]);

  useEffect(() => {
    if(pc.current) {
      if (!receivedWebsocketMessage) {
        console.log('Received empty data, skipping');
        return;
      }
      const data = JSON.parse(receivedWebsocketMessage);
      if (!Object.prototype.hasOwnProperty.call(data, 'sdp')) {
        console.log('sdp not present, skipping');
        return;
      }
      pc.current
        .setRemoteDescription(new RTCSessionDescription(data))
        .then(() => {
          console.log('setRemoteDescription complete, creating answer');
          pc.current
            .createAnswer()
            .then((sessionDescription) => {
              console.log('createAnswer complete, setLocalDescription');
              pc.current
                .setLocalDescription(sessionDescription)
                .then(() => {
                  console.log('setLocalDescription complete, sendAnswer');
                  const answerPayload = {
                    sessionDescription: pc.current.localDescription,
                  };
                  axios
                    .post(
                      `${uiServerOrVstEndpointString}/setAnswer?peerid=${pc.current.peerId.toString()}`,
                      answerPayload
                    )
                    .then(() => {
                      console.log('setAnswer success');
                    })
                    .catch((e) => {
                      console.error('setAnswer error: ', e);
                      enqueueSnackbar(`setAnswer error`, {
                        variant: 'error',
                        anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                      });
                    });
                })
                .catch((e) => {
                  console.error('setLocalDescription error: ', e);
                  enqueueSnackbar(`setLocalDescription error`, {
                    variant: 'error',
                    anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
                  });
                });
            })
            .catch((e) => {
              console.error('createAnswer error: ', e);
              enqueueSnackbar(`createAnswer error`, {
                variant: 'error',
                anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
              });
            });
        })
        .catch((e) => {
          console.error('setRemoteDescription error: ', e);
          enqueueSnackbar(`setRemoteDescription error`, {
            variant: 'error',
            anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          });
        });
    }
  }, [receivedWebsocketMessage])
  

  useEffect(() => {
    let timer;
    console.log("counter: ", counter);
    if (config.enableEngress) {
      timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
      if (counter === 2) {
        console.debug(
          "Terminating peer connection in 9 seconds after countdown expires"
        );
        setTimeout(() => {
          // eslint-disable-next-line no-use-before-define
          stopWebrtcConnection();
        }, 9000);
      }
    }
    return () => clearInterval(timer);
  }, [counter]);

  const openPermissionDialog = () => {
    setOpen(true);
  };

  const closePermissionDialog = (event, reason) => {
    if (reason == null) setOpen(false);
    else if (reason !== "escapeKeyDown" && reason !== "backdropClick") {
      setOpen(false);
    }
  };

  const enableAudio = () => {
    if (avatarVideoRef.current) {
      console.debug("enabling audio...");
      avatarVideoRef.current.muted = false;
    }
  };

  const addIceCandidate = (candidate) => {
    const jsonData = {
      peerid: pc.current.peerId.toString(),
      candidate,
    };
    console.debug(
      "calling vms/addIceCandidate with ",
      jsonData
    );
    const promise = axios(
      `${uiServerOrVstEndpointString}/addIceCandidate`,
      {
        method: "post",
        data: jsonData,
      }
    );
    promise.then((response) => {
      console.log("added ice candidate ?", response.data);
    });
    promise.catch((e) => {
      console.error("add ice candidate error ", e);
    });

    return () => {
      clearInterval(webrtcStatsRef.current);
    };
  };

  const onReceiveCandidate = (response) => {
    const candidates = response.data;
    console.debug(
      `Received candidates from VMS: ${JSON.stringify(candidates)}`
    );
    if (candidates) {
      console.debug(
        "Creating RTCIceCandidate from each received candidate.."
      );
      for (let i = 0; i < candidates.length; i += 1) {
        const candidate = new RTCIceCandidate(candidates[i]);

        console.debug(
          `Adding ICE candidate - ${i} :${JSON.stringify(candidate)}`
        );
        pc.current.addIceCandidate(
          candidate,
          () => {
            console.debug(`addIceCandidate OK - ${i}`);
          },
          (error) => {
            console.debug(`addIceCandidate error - ${i}`, error);
          }
        );
      }
    }
  };

  const getIceCandidate = () => {
    console.debug("calling api/getIceCandidate");
    axios
      .get(
        `${uiServerOrVstEndpointString}/getIceCandidate?peerid=${pc.current.peerId}`
      )
      .then(onReceiveCandidate)
      .catch((e) => {
        console.error("getIceCandidate error", e);
      });
  };

  const toggleMicrophone = (val) => {
    console.log("Toggling microphone")
    if(stream && stream.getAudioTracks()[0]) {
      stream.getAudioTracks()[0].enabled = val;
    }
  }

  const setRemoteDescription = (sessionDescriptionAnswer) => {
    sessionDescriptionAnswer.sdp = sessionDescriptionAnswer.sdp.replace(`useinbandfec=1', 'useinbandfec=1; maxaveragebitrate=${Number(audioBitrate.current)*2}`);
    pc.current
      .setRemoteDescription(new RTCSessionDescription(sessionDescriptionAnswer))
      .then(() => {
        earlyCandidates.current.forEach(addIceCandidate);
        getIceCandidate();
      })
      .catch((e) => {
        console.error("onReceiveCall: ", e);
      });
  };

  const sendSessionDescriptionToVst = (sessionDescription) => {
    const sessionDescriptionPayload = {
      peerid: pc.current.peerId.toString(),
      is_client: true,
      options: {
        quality: "auto",
        rtptransport: "udp",
        timeout: 60,
      },
      sessionDescription,
    };
    console.debug(
      "Payload of stream start: ",
      sessionDescriptionPayload
    );

    const promise = axios(
      `${uiServerOrVstEndpointString}/stream/start`,
      {
        method: "post",
        data: sessionDescriptionPayload,
      }
    );
    promise.then((response) => {
      const sessionDescriptionAnswer = response.data;
      console.debug(
        "Received response of stream start: ",
        sessionDescriptionAnswer
      );
      console.debug(
        "Signaling state is ",
        pc.current.signalingState
      );
      if (pc.current.signalingState === "have-local-offer") {
        console.debug("OK signalingState");
      } else {
        console.debug("unexpected signalingState");
      }
      setRemoteDescription(sessionDescriptionAnswer);
    });
    promise.catch((e) => {
      console.error("wertc error: ", e);
      dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.ERROR));
      // eslint-disable-next-line no-use-before-define
      restartWebRtcConnection();
    });
  };

  const rewriteSdp = (sdp) => {
    const sdpStringFind = 'a=fmtp:(.*) (.*)';
    let sdpStringReplace = null;
    sdpStringReplace = `a=fmtp:$1 $2;x-google-max-bitrate=${window.maxBitrate};x-google-min-bitrate=${window.minBitrate};x-google-start-bitrate=${window.startBitrate}`;
    let newSDP = sdp.sdp.toString();
    newSDP = newSDP.replace(new RegExp(sdpStringFind, 'g'), sdpStringReplace);
    // eslint-disable-next-line no-param-reassign
    sdp.sdp = newSDP;
    console.debug('using modified sdp: ', sdp);
    return sdp;
  };

  const createOffer = () => {
    // add tracks before creating offer
    stream.getTracks().forEach((track) => pc.current.addTrack(track, stream));
    pc.current
      .createOffer(OFFER_OPTIONS)
      .then((sessionDescription) => {
        if (window.minBitrate !== 0 && window.startBitrate !== 0 && window.maxBitrate !== 0) {
          sessionDescription = rewriteSdp(sessionDescription);
        }
        console.debug("session Description", sessionDescription);
        pc.current.setLocalDescription(sessionDescription);
        return sessionDescription;
      })
      .then((sessionDescription) => {
        sendSessionDescriptionToVst(sessionDescription);
      })
      .catch((error) => {
        console.error("Failed to create offer", error);
      });
  };

  const webrtcStatsHelper = () => {
    webrtcStatsRef.current = setInterval(() => {
      try {
        pc.current.getStats(null).then((stats) => {
          stats.forEach((report) => {
            if (report.type === "inbound-rtp" && report.kind === "video") {
              if (
                Object.prototype.hasOwnProperty.call(report, "framesPerSecond")
              ) {
                if (report.framesPerSecond > 0) {
                  clearInterval(webrtcStatsRef.current);
                  if (config.enableLogs)
                    console.info(
                      "Webrtc client is receiving frames"
                    );
                }
              }
            }
          });
        });
      } catch (error) {
        console.error("Webrtc error", error);
      }
    }, 2000);
  };

  const playVideoAndTestFps = () => {
    setPlayingStatus("avatar-playing");
    /* Try to unmute video */
    avatarVideoRef.current.muted = false;
    const playPromise = avatarVideoRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          if (config.enableLogs)
            console.info("Autoplay with audio successful");
        })
        .catch((error) => {
          console.error("Autoplay with audio failed", error);
          avatarVideoRef.current.muted = true;
          avatarVideoRef.current.play();
          openPermissionDialog();
        });
    }
    webrtcStatsHelper();
  };

  const onConnectionStateChange = () => {
    // FireFox does not support onConnectionStateChange event
    console.info(
      "connection state change: ",
      pc.current.connectionState
    );
    if (pc.current.connectionState === "disconnected") {
      console.debug("Lost peer connection...");
    }
  };

  const onSignalingStateChange = () => {
    if (config.enableLogs)
      console.info(
        "signaling state change: ",
        pc.current.signalingState
      );
  };

  const onIceGatheringStateChange = () => {
    if (config.enableLogs)
      console.info(
        "gathering state change: ",
        pc.current.iceGatheringState
      );
  };

  const onIceCandidateError = (e) => {
    console.debug("onIceCandidateError: ", e);
    if (e.errorCode !== 701) {
      console.error(
        `${e.errorText} error code ${e.errorCode} and url ${e.url}`,
        "onIceCandidateError"
      );
    }
    if (e.errorCode === 701) {
      console.debug(
        "error code is 701 that means DNS failed for ipv6, harmless error"
      );
    }
  };

  const onIceConnectionStateChange = () => {
    if (config.enableLogs)
      console.info(
        "ice connection state change: ",
        pc.current.iceConnectionState
      );
    if (pc.current && pc.current.iceConnectionState === "new") {
      getIceCandidate();
    }
    if (pc.current.iceConnectionState === "connected") {
      dispatch(webSocketActions.setStreamId(window.tokkioPeerId));
      dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.CONNECTED));
      restartCount.current = 0;
      playVideoAndTestFps();
    }
    if (pc.current.iceConnectionState === "disconnected") {
      setPlayingStatus("avatar-not-playing");
      dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.ERROR));
    }
    if (pc.current.iceConnectionState === "failed") {
      setPlayingStatus("avatar-not-playing");
      dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.ERROR));
      // eslint-disable-next-line no-use-before-define
      restartWebRtcConnection();
    }
  };

  const onTrack = (event) => {
    // eslint-disable-next-line no-shadow
    const [stream] = event.streams;
    avatarVideoRef.current.srcObject = stream;
  };

  const onIceCandidate = (event) => {
    if (!event.candidate) {
      console.debug(
        "received null candidate, that means its the last candidate"
      );
      return;
    }
    if (event.candidate) {
      if (event.candidate.candidate.length === 0) {
        console.debug(
          "Recevived empty string for candidate - client is firefox"
        );
        return;
      }
      console.debug(
        "received candidate inside onIceCandidate callback: ",
        event.candidate.candidate
      );
      // If a srflx candidate was found, notify that the STUN server works!
      if (event.candidate.type === "srflx") {
        console.debug(
          "The STUN server is reachable for this candidate!"
        );
        console.debug(`Public IP Address is: ${event.candidate.address}`);
      }
      // If a relay candidate was found, notify that the TURN server works!
      if (event.candidate.type === "relay") {
        console.debug(
          "The TURN server is reachable for this candidate!"
        );
      }
      if (pc.current && pc.current.currentRemoteDescription) {
        addIceCandidate(event.candidate);
      } else {
        earlyCandidates.current.push(event.candidate);
      }
    }
  };

  const createRTCPeerConnection = (iceServers) => {
    if (config.enableLogs)
      console.debug("ICE Servers: ", iceServers);
    try {
      const peerConnection = new RTCPeerConnection(
        { iceServers },
        PEER_CONNECTION_OPTIONS
      );
      peerConnection.peerId = peerId.current;
      peerConnection.onicecandidate = onIceCandidate;
      peerConnection.ontrack = onTrack;
      peerConnection.oniceconnectionstatechange = onIceConnectionStateChange;
      peerConnection.onicecandidateerror = onIceCandidateError;
      peerConnection.onicegatheringstatechange = onIceGatheringStateChange;
      peerConnection.onsignalingstatechange = onSignalingStateChange;
      peerConnection.onconnectionstatechange = onConnectionStateChange;
      pc.current = peerConnection;
    } catch (error) {
      console.error(`Failed to create RTC peer connection.`, error);
      dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.ERROR));
    }

    createOffer();
  };

  const onDeviceFetch = () => {
    peerId.current = window.tokkioPeerId;
    axios
      .get(
        `${uiServerOrVstEndpointString}/getIceServers?peerId=${peerId.current}`
      )
      .then((response) => {
        console.log(response.data);
        createRTCPeerConnection(response.data.iceServers);
      })
      .catch((rejectedValueOrSerializedError) => {
        console.error(rejectedValueOrSerializedError);
        dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.ERROR));
      });
  };

  const getVstConfig = async () => {
    await axios
    .get(
      `${uiServerOrVstEndpointString}/vst/settings`
    )
    .then((response) => {
      console.log(response.data);
      // read VST settings and set webrtc sender parameters
      const vstSettings = response.data;
      if(Object.prototype.hasOwnProperty.call(vstSettings, "webrtc_in_audio_sender_max_bitrate")) {
        audioBitrate.current = vstSettings.webrtc_in_audio_sender_max_bitrate;
      }
      if(Object.prototype.hasOwnProperty.call(vstSettings, "webrtc_in_video_degradation_preference")) {
        degradationPreference.current = vstSettings.webrtc_in_video_degradation_preference ;
      }
      if(Object.prototype.hasOwnProperty.call(vstSettings, "webrtc_in_video_sender_max_framerate")) {
        maxFps.current = vstSettings.webrtc_in_video_sender_max_framerate;
      }
      window.minBitrate = response.data.webrtc_min_birate || 0;
      window.maxBitrate = response.data.webrtc_max_birate || 0;
      window.startBitrate = response.data.webrtc_start_birate || 0;
      console.log("audioBitrate", audioBitrate.current);
      console.log("degradationPreference", degradationPreference.current);
      console.log("maxFps", maxFps.current);
      console.log('using bitrates', window.minBitrate, window.maxBitrate, window.startBitrate);
    })
    .catch((rejectedValueOrSerializedError) => {
      console.error(rejectedValueOrSerializedError);
    });
  }

  const handleRecording = async () => {
    dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.PENDING));
    await getVstConfig();
    /**
     * get 720p, 1080p resolution from webcam.
     * enable echoCancellation explicitly
     */
    const constraints = {
      audio: { echoCancellation: true },
      video: {
        width: { min: 1280, ideal: 1280, max: 1920 },
        height: { min: 720, ideal: 720, max: 1080 },
        frameRate: {
          ideal: 30,
          max: 30,
        },
      },
    };

    if(config.enableCamera === false){
      constraints.video = false
    }


    if(config.enableCamera === true && maxFps.current != null) {
      constraints.video.frameRate = {
        ideal : maxFps.current,
        max: maxFps.current
      }
    }
    console.log("constraints: ", constraints);
    navigator.mediaDevices
      .getUserMedia(constraints)
      // eslint-disable-next-line no-use-before-define
      .then(setContentHint)
      .catch((err) => {
        console.log("Failed to access mic", err);
        enqueueSnackbar("Failed to access mic", { variant: "error" });
        dispatch(webrtcActions.setWebrtcStatus(WEBRTC_STATUS.ERROR));
      });
  };

  const setContentHint = (CameraStream) => {
    const videoTracks = CameraStream.getVideoTracks();
    const audioTracks = CameraStream.getAudioTracks();
    if (videoTracks.length > 0) {
      console.log(`Using video device: ${videoTracks[0].label}`);
    }
    if (audioTracks.length > 0) {
      console.log(`Using audio device: ${audioTracks[0].label}`);
    }
    /**
     * The track should be treated as if video details are extra important.
     * This is generally applicable to presentations or web pages with text
     * content, painting or line art. This setting would normally optimize
     * for detail in the resulting individual frames rather than smooth playback.
     * Artefacts from quantization or downscaling that make small text or line
     * art unintelligible should be avoided.
     */
    videoTracks.forEach((track) => {
      if(degradationPreference.current != null) {
        console.log("setting degradation preference: ", degradationPreference.current);
        track.contentHint = degradationPreference.current === "resolution" ? "motion" : "detail";
      }
      else {
        // default setting is to reduce resolution
        console.log("setting default degradation preference");
        track.contentHint = "motion";
      }
      console.log("video track: ", track);
    });
    /**
     * The track should be treated as if it contains speech data.
     * Consuming this signal it may be appropriate to apply noise
     * suppression or boost intelligibility of the incoming signal.
     */
    audioTracks.forEach((track) => {
      track.contentHint = "speech";
      console.log("audio track: ", track);
    });
    stream = CameraStream;
    onDeviceFetch();
  };

  const startWebrtcConnection = () => {
    handleRecording();
  };

  const stopWebrtcConnection = async () => {
    if (pc.current != null) {
      const jsonData = {
        peerid: pc.current.peerId.toString(),
      };
      pc.current.close();
      pc.current = null;
      peerId.current = null;
      earlyCandidates.current = [];
      avatarVideoRef.current.srcObject = null;
      console.debug("calling api/stream/stop with ", jsonData);
      const promise = await axios(
        `${uiServerOrVstEndpointString}/stream/stop`,
        {
          method: "post",
          data: jsonData,
        }
      );
      promise.then((response) => {
        console.log("stream stop ?", response.data);
      });
      promise.catch((e) => {
        console.error("stream stop error", e);
      });
    }
  };

  const restartWebRtcConnection = () => {
    if (restartCount.current < MAX_RETRIES) {
      // add delay to avoid page hang
      setTimeout(async () => {
        await stopWebrtcConnection();
        startWebrtcConnection();
        restartCount.current += 1;
      }, 500);
      console.debug("RESTARTING WEBRTC CONNECTION !!!");
    } else {
      console.debug(
        "WEBRTC MAX RETRY LIMIT REACHED, RESTART PAGE !!!"
      );
      enqueueSnackbar("Unable to stream avatar, please refresh page", {
        variant: "error",
      });
    }
  };

  return (
    <Grid item xs={12}>
      <div id='video-container'>
        <div style={videoPlayerStyles} id={isVideoPlaying}>
          <video
            ref={avatarVideoRef}
            autoPlay
            controls={false}
            height={pMode ? "100%" : "570px"}
            width={pMode ? "100%" : "1013px"}
            muted
            id='animated-avatar'
          />
          <VideoCaption />
          {webrtcStatus === WEBRTC_STATUS.PENDING && (
            <Grid
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
              alignItems='center'
            >
              <CircularProgress />
            </Grid>
          )}
          {webrtcStatus === WEBRTC_STATUS.ERROR && (
            <Grid
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
              alignItems='center'
            >
              <ErrorOutlineIcon color='error' fontSize='large' />
            </Grid>
          )}
        </div>
      </div>
      <div>
        <Dialog
          open={open}
          onClose={closePermissionDialog}
          disableEscapeKeyDown
        >
          <DialogTitle id='permission-alert'>
            Enable video auto play with sound
          </DialogTitle>
          <DialogContent>
            <DialogContentText id='permission-alert-description'>
              Let Tokkio UI play streams with audio. Without this permission
              video playback will be muted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closePermissionDialog}>Disagree</Button>
            <Button
              onClick={() => {
                closePermissionDialog();
                enableAudio();
              }}
              autoFocus
            >
              Agree
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Grid>
  );
}

export default Webrtc;
